import { useState, useMemo, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card } from "../../../components/ui/card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faMoneyBillWave,
  faUser,
  faFileAlt,
  faChartLine,
  faSpinner,
  faFilter,
  faTimes,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import AddSalesCloseout from "./AddSalesCloseout";
import EditSalesCloseout from "./EditSalesCloseout";
import { useAuth, useSaleCloseouts, useShopSellers } from "../../../hooks";

const SalesCloseoutTab = ({ shop }) => {
  const shopId = shop?.shopId;
  const { hasPermission, user } = useAuth();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCloseout, setSelectedCloseout] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // Advanced Filter Modal State
  const [isAdvFilterOpen, setIsAdvFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    startDate: "",
    endDate: "",
  });

  const {
    saleCloseouts,
    pagination,
    totals,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    setStartDate: setHookStartDate,
    setEndDate: setHookEndDate,
    createSaleCloseout,
    updateSaleCloseout,
    loading,
  } = useSaleCloseouts(shopId);

  const { sellers, loading: sellersLoading } = useShopSellers(shopId, {
    per_page: 10,
  });

  const isSeller = user?.role === "seller";

  const mySellerId = useMemo(() => {
    if (!isSeller) return null;
    if (!Array.isArray(sellers)) return null;

    const mine = sellers.find(
      (s) => (s.user?.username ?? s.username) === user?.username,
    );

    if (!mine) return null;

    return String(mine.shop_saller_id ?? mine.shopSellerId ?? "");
  }, [isSeller, sellers, user?.username]);

  const canManageCloseouts =
    hasPermission([
      "manage_all",
      "manage_sales_closeout",
      "manage_shop_modules",
    ]) &&
    hasPermission([
      "manage_business_seller",
      "read_business_seller",
      "manage_user_account",
      "read_user_account",
    ]);

  const canEditRow = (closeout) => {
    if (!canManageCloseouts) return false;
    if (isSeller && String(closeout.shopSellerId) !== String(mySellerId)) {
      return false;
    }
    return true;
  };

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm, setSearchTerm, setCurrentPage]);

  const closeouts = useMemo(() => {
    return (saleCloseouts || []).map((closeout) => {
      const shopSeller = closeout?.shopSeller || closeout?.shop_seller || null;
      const sellerUser = shopSeller?.user || null;

      const sellerFullName = sellerUser?.full_name || "";
      const sellerUsername = sellerUser?.username || shopSeller?.username || "";
      const sellerPhone = sellerUser?.phone || "";

      const sellerName = sellerFullName || sellerUsername || "-";

      const shopSellerId =
        closeout?.shopSellerId ??
        closeout?.shop_seller_id ??
        shopSeller?.shopSellerId ??
        shopSeller?.shop_seller_id ??
        shopSeller?.shop_saller_id ??
        shopSeller?.id ??
        "";

      const closeoutId =
        closeout?.closeoutId ?? closeout?.closeout_id ?? closeout?.id ?? "";

      const amount =
        closeout?.amount !== undefined && closeout?.amount !== null
          ? closeout.amount
          : 0;

      const createdAt =
        closeout?.created_at ?? closeout?.createdAt ?? closeout?.date ?? "";

      const description = closeout?.description ?? "";

      return {
        ...closeout,
        closeoutId,
        shopSellerId,
        amount,
        created_at: createdAt,
        description,
        sellerName,
        sellerFullName,
        sellerUsername,
        sellerPhone,
      };
    });
  }, [saleCloseouts]);

  const totalItems = pagination?.total ?? pagination?.meta?.total ?? 0;

  const totalPages =
    pagination?.lastPage ??
    pagination?.last_page ??
    pagination?.meta?.last_page ??
    pagination?.meta?.lastPage ??
    1;

  const currentPageValue =
    pagination?.currentPage ??
    pagination?.current_page ??
    pagination?.meta?.current_page ??
    currentPage;

  const from = pagination?.from ?? pagination?.meta?.from ?? 0;
  const to = pagination?.to ?? pagination?.meta?.to ?? 0;

  const startIndex = from > 0 ? from - 1 : (currentPageValue - 1) * pageSize;

  const formatCurrency = (amount) => {
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(amount || 0)) + " TSH"
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return "-";

    return parsedDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleAddCloseout = async (data) => {
    await createSaleCloseout({
      shopSellerId: data.shopSellerId,
      shopId,
      amount: Number(data.amount),
      created_at: data.created_at,
      description: data.description?.trim() || null,
    });

    setIsAddModalOpen(false);
  };

  const handleUpdateCloseout = async (data) => {
    const closeoutId =
      data?.closeoutId || data?.id || selectedCloseout?.closeoutId;

    if (!closeoutId) return;

    await updateSaleCloseout(closeoutId, {
      shopSellerId:
        data.shopSellerId || data.sellerId || selectedCloseout?.shopSellerId,
      shopId,
      amount: Number(data.amount),
      created_at: data.created_at || selectedCloseout?.created_at,
      description: data.description?.trim() || null,
    });

    setIsEditModalOpen(false);
    setSelectedCloseout(null);
  };

  const handleOpenEdit = (closeout) => {
    setSelectedCloseout({
      closeoutId: closeout.closeoutId || "",
      shopSellerId: closeout.shopSellerId || "",
      amount:
        closeout.amount !== undefined && closeout.amount !== null
          ? Number(closeout.amount)
          : 0,
      created_at: closeout.created_at || "",
      description: closeout.description || "",
      sellerName: closeout.sellerName || "",
      ...closeout,
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setSelectedCloseout(null);
  };

  // --- Advanced Filter Handlers ---
  const openAdvFilter = () => {
    setTempFilters({
      startDate: startDate || "",
      endDate: endDate || "",
    });
    setIsAdvFilterOpen(true);
  };

  const applyAdvFilters = () => {
    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setHookStartDate(tempFilters.startDate);
    setHookEndDate(tempFilters.endDate);
    setIsFilterApplied(!!(tempFilters.startDate || tempFilters.endDate));
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const clearAdvFilters = () => {
    setTempFilters({
      startDate: "",
      endDate: "",
    });
    setStartDate("");
    setEndDate("");
    setHookStartDate("");
    setHookEndDate("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const summaryStats = useMemo(() => {
    if (totals && (totals.totalAmount || totals.totalCloseouts)) {
      return {
        total: totals.totalCloseouts ?? 0,
        totalAmount: Number(totals.totalAmount ?? 0),
      };
    }

    const list = closeouts || [];
    return {
      total: totalItems || list.length,
      totalAmount: list.reduce((s, c) => s + Number(c.amount || 0), 0),
    };
  }, [totals, closeouts, totalItems]);

  const MobileCloseoutCard = ({ closeout, index }) => {
    return (
      <div className="bg-white border-2 border-blue-900 rounded-md p-4 shadow-sm transition-all duration-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-white font-medium bg-blue-900 px-2 rounded-full">
                {startIndex + index + 1}
              </span>

              <span className="text-xs text-gray-600">
                {formatDate(closeout.created_at)}
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5">
              <FontAwesomeIcon
                icon={faMoneyBillWave}
                className="h-3 w-3 text-gray-600"
              />
              <span className="font-medium text-sm">Amount:</span>
              <span className="text-green-600 font-bold text-sm">
                {formatCurrency(closeout.amount)}
              </span>
            </div>

            <div className="flex items-start gap-1.5 text-xs text-gray-600 mb-1.5">
              <FontAwesomeIcon
                icon={faUser}
                className="h-3 w-3 text-gray-600 mt-0.5"
              />
              <span className="font-medium text-sm">Seller:</span>
              <div className="flex flex-col">
                <span className="text-gray-700 text-sm font-medium">
                  {closeout.sellerFullName || "-"}
                </span>
                {closeout.sellerPhone && (
                  <span className="text-gray-600 text-xs">
                    {closeout.sellerPhone}
                  </span>
                )}
                {closeout.sellerUsername && (
                  <span className="text-gray-500 text-xs">
                    {closeout.sellerUsername}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-start gap-1.5 text-xs text-gray-600">
              <FontAwesomeIcon
                icon={faFileAlt}
                className="h-3 w-3 text-gray-600 mt-0.5"
              />
              <span className="font-medium text-sm">Note:</span>
              <span className="text-gray-600 text-sm truncate">
                {closeout.description || "-"}
              </span>
            </div>
          </div>

          {canEditRow(closeout) && (
            <div className="flex flex-col gap-1.5 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleOpenEdit(closeout)}
                title="Edit Closeout"
                className="gap-1 border-blue-600 text-blue-600 hover:bg-blue-50 hover:border-blue-700 px-2.5 py-1 h-8 text-xs"
              >
                <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />
                <span className="hidden xs:inline">Edit</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!shopId) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-sm">
            Shop information is not available.
          </p>
        </div>
      </div>
    );
  }

  if (loading || sellersLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading sales closeouts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faChartLine}
              className="text-blue-800 h-4 w-4"
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Sales Closeout
            </h3>
            <p className="text-sm text-gray-600">
              Record daily sales closing - total money collected per seller
            </p>
          </div>
        </div>

        {canManageCloseouts && (
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Closeout
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <p className="text-xs text-gray-600 font-medium">Total Closeouts</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {summaryStats.total}
          </p>
          <Badge
            variant="outline"
            className="mt-0.5 sm:mt-1 text-xs bg-blue-800 text-white border-blue-200"
          >
            Records
          </Badge>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <p className="text-xs text-gray-600 font-medium">Total Revenue</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
            {formatCurrency(summaryStats.totalAmount)}
          </p>
          <Badge
            variant="outline"
            className="mt-0.5 sm:mt-1 text-xs bg-green-600 text-white border-green-200"
          >
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="mr-0.5 h-2 w-2"
            />
            Total
          </Badge>
        </Card>
      </div>

      {/* Main Toolbar: Search Left, Advanced Filter Right */}
      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={localSearchTerm}
          onChange={(value) => setLocalSearchTerm(value)}
          placeholder="Search by seller, description, or amount..."
          className="flex-1"
        />
        <Button
          onClick={openAdvFilter}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6"
        >
          <FontAwesomeIcon icon={faFilter} />
          Advanced Filter
          {isFilterApplied && (
            <span className="ml-1 w-2 h-2 rounded-full bg-orange-400"></span>
          )}
        </Button>
      </div>

      <div className="hidden md:block">
        <TableWrapper
          title="closeouts"
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          className="bg-white border border-gray-200 shadow-sm"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  #
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Date
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Seller
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Username
                </th>
                <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Amount
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden lg:table-cell">
                  Description
                </th>
                {canManageCloseouts && (
                  <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                    Actions
                  </th>
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {closeouts.length > 0 ? (
                closeouts.map((closeout, i) => {
                  const globalIndex = startIndex + i + 1;

                  return (
                    <tr
                      key={closeout.closeoutId || i}
                      className="hover:bg-blue-50/50 transition-colors"
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs text-gray-600 font-medium">
                        {globalIndex}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600">
                        {formatDate(closeout.created_at)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-800">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {closeout.sellerFullName || "-"}
                          </span>
                          {closeout.sellerPhone && (
                            <span className="text-sm text-blue-900">
                              {closeout.sellerPhone}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-800">
                        {closeout.sellerUsername && (
                          <span className="text-sm text-gray-600 font-medium">
                            {closeout.sellerUsername}
                          </span>
                        )}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm font-bold text-green-600">
                        {formatCurrency(closeout.amount)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden lg:table-cell max-w-[200px] truncate">
                        {closeout.description || "-"}
                      </td>
                      {canManageCloseouts && (
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex gap-1 sm:gap-2 justify-center">
                            {canEditRow(closeout) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-blue-600 hover:bg-blue-50"
                                onClick={() => handleOpenEdit(closeout)}
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="h-3 w-3 sm:h-4 sm:w-4"
                                />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={canManageCloseouts ? 7 : 6}
                    className="text-center py-6 sm:py-8"
                  >
                    <EmptyState
                      title="No sales closeouts found"
                      message={
                        searchTerm
                          ? "Try adjusting your search terms"
                          : canManageCloseouts
                            ? "Add your first sales closeout to get started"
                            : "No sales closeouts yet"
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableWrapper>
      </div>

      <div className="md:hidden">
        {closeouts.length > 0 ? (
          <>
            <div className="flex items-center justify-between text-xs text-gray-600 mb-3 px-1">
              <span>
                Showing {from}-{to} of {totalItems} records
              </span>
              <span>
                Page {currentPageValue} of {totalPages || 1}
              </span>
            </div>

            <div className="space-y-3">
              {closeouts.map((closeout, i) => (
                <MobileCloseoutCard
                  key={closeout.closeoutId || i}
                  closeout={closeout}
                  index={i}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <EmptyState
              title="No sales closeouts found"
              message={
                searchTerm
                  ? "Try adjusting your search terms"
                  : canManageCloseouts
                    ? "Add your first sales closeout to get started"
                    : "No sales closeouts yet"
              }
            />
          </div>
        )}
      </div>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {canManageCloseouts && (
        <AddSalesCloseout
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddCloseout}
          sellers={sellers}
          isSeller={isSeller}
          mySellerId={mySellerId}
        />
      )}

      {canManageCloseouts && (
        <EditSalesCloseout
          isOpen={isEditModalOpen}
          onClose={handleCloseEdit}
          onUpdate={handleUpdateCloseout}
          closeout={selectedCloseout}
          sellers={sellers}
        />
      )}

      {/* Advanced Filter Modal */}
      {isAdvFilterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Advanced Filter
              </h3>
              <button
                onClick={() => setIsAdvFilterOpen(false)}
                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-blue-600 h-4 w-4"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Filter Closeouts
                  </h4>
                  <p className="text-xs text-gray-600">
                    Select criteria to narrow down results
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Filter by Date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      From
                    </label>
                    <input
                      type="date"
                      value={tempFilters.startDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      To
                    </label>
                    <input
                      type="date"
                      value={tempFilters.endDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={clearAdvFilters}
                className="border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none px-6"
              >
                Clear All
              </Button>
              <Button
                onClick={applyAdvFilters}
                className="bg-blue-800 hover:bg-blue-900 text-white px-6 shadow-sm"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCloseoutTab;
